import React from "react";
import { Input } from "./ui/input";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Search, Loader2, SendHorizontal } from "lucide-react";
import posthog from "posthog-js";
import { motion } from "framer-motion";

type SearchBarProps = {
    variant?: "fixed" | "inline";
};

export const SearchBar: React.FC<SearchBarProps> = ({ variant }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const initialQ = searchParams.get("q") || "";
    const [query, setQuery] = React.useState(initialQ);
    const [isFocused, setIsFocused] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    // Keep the input synced when the URL changes (back/forward/navigation)
    React.useEffect(() => {
        const q = new URLSearchParams(location.search).get("q") || "";
        setQuery(q);
    }, [location.search]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (query.trim()) {
            setIsLoading(true);
            posthog.capture("video_search", { query });
            // Simulate a small delay for effect or just navigate immediately
            navigate(`/gallery?q=${encodeURIComponent(query)}`);
            setIsLoading(false);
        }
    };

    const resolvedVariant =
        variant ?? (location.pathname === "/" ? "inline" : "fixed");

    // Inline (centered) variant used on the homepage
    if (resolvedVariant === "inline") {
        return (
            <div className="w-full">
                <div className="max-w-3xl mx-auto px-4">
                    <motion.form
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.4 }}
                        onSubmit={handleSubmit}
                        className="relative group"
                        aria-label="Search form"
                    >
                        {/* Glow effect */}
                        <div
                            className={`absolute -inset-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full opacity-20 blur transition duration-500 group-hover:opacity-40 ${
                                isFocused ? "opacity-60" : ""
                            }`}
                        />

                        <div
                            className={`
                                relative flex items-center rounded-full px-6 py-4
                                bg-background/80 backdrop-blur-xl border border-white/10
                                shadow-2xl transition-all duration-300
                            `}
                        >
                            <Search
                                className={`w-5 h-5 mr-4 transition-colors ${
                                    isFocused
                                        ? "text-primary"
                                        : "text-muted-foreground"
                                }`}
                            />

                            <Input
                                type="text"
                                placeholder="Describe what you're looking for..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                                className="flex-1 bg-transparent placeholder:text-muted-foreground/50 focus:outline-none border-0 text-lg h-auto p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                                aria-label="Search"
                            />

                            <button
                                type="submit"
                                disabled={!query.trim() || isLoading}
                                className={`
                                    ml-2 p-2 rounded-full transition-all duration-200
                                    ${
                                        query.trim()
                                            ? "text-primary hover:bg-primary/10"
                                            : "text-muted-foreground/30"
                                    }
                                `}
                                aria-label="Execute search"
                            >
                                {isLoading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <SendHorizontal className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </motion.form>
                </div>
            </div>
        );
    }

    // Fixed variant used on non-home pages
    return (
        <form
            onSubmit={handleSubmit}
            className="relative w-full"
            aria-label="Search form"
        >
            <div
                className={`
                    flex items-center h-10 sm:h-11 rounded-xl px-3 
                    transition-all duration-200 
                    bg-muted/50 hover:bg-muted/80 focus-within:bg-background focus-within:ring-2 focus-within:ring-primary/20
                    border border-transparent focus-within:border-primary/20
                `}
            >
                <Search className="w-4 h-4 text-muted-foreground mr-2" />
                <Input
                    type="text"
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-1 min-w-0 bg-transparent placeholder:text-muted-foreground focus:outline-none border-0 text-sm h-full p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    aria-label="Search"
                />
                {isLoading && (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground ml-2" />
                )}
            </div>
        </form>
    );
};
